import { useState, useEffect } from 'react';
import { HelpDrawer } from '../../components/HelpDrawer';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Mail, MessageCircle, CheckCircle, XCircle, RefreshCw, Send,
  ExternalLink, Shield, Activity, Info, Zap, Globe, Key, Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

function StatusBadge({ configured, loading }) {
  if (loading) return <Badge className="bg-gray-100 text-gray-500 gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Verificando...</Badge>;
  return configured
    ? <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle className="w-3 h-3" /> Conectado</Badge>
    : <Badge className="bg-red-100 text-red-700 gap-1"><XCircle className="w-3 h-3" /> No configurado</Badge>;
}

export default function AdminIntegrations() {
  const [status, setStatus] = useState({ kapso: { configured: false }, sendpulse: { configured: false } });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [testingKapso, setTestingKapso] = useState(false);
  const [testingSP, setTestingSP] = useState(false);
  const [kapsoChannels, setKapsoChannels] = useState([]);
  const [testWhatsApp, setTestWhatsApp] = useState({ phone: '', message: 'Hola! Mensaje de prueba desde OkReserva 🍽️', channel_id: '' });

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/integrations/status`, { headers: authHeader() });
      if (res.ok) setStatus(await res.json());
    } catch { }
    finally { setLoadingStatus(false); }
  };

  const loadKapsoChannels = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/integrations/kapso/channels`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setKapsoChannels(data.channels || data.data || []);
      }
    } catch { }
  };

  const testKapso = async () => {
    setTestingKapso(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/integrations/kapso/test`, { method: 'POST', headers: authHeader() });
      const data = await res.json();
      if (data.ok) {
        toast.success('Kapso conectado correctamente');
        loadKapsoChannels();
        loadStatus();
      } else {
        toast.error(`Error Kapso: ${data.message}`);
      }
    } catch { toast.error('Error de conexión'); }
    finally { setTestingKapso(false); }
  };

  const testSendPulse = async () => {
    setTestingSP(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/integrations/sendpulse/test`, { method: 'POST', headers: authHeader() });
      const data = await res.json();
      if (data.ok) {
        toast.success('SendPulse conectado correctamente');
        loadStatus();
      } else {
        toast.error(`Error SendPulse: ${data.message}`);
      }
    } catch { toast.error('Error de conexión'); }
    finally { setTestingSP(false); }
  };

  const sendTestWhatsApp = async () => {
    if (!testWhatsApp.phone) { toast.error('Ingresa un número de teléfono'); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/integrations/kapso/send-whatsapp`, {
        method: 'POST', headers: authHeader(),
        body: JSON.stringify(testWhatsApp)
      });
      const data = await res.json();
      if (data.ok || data.status < 300) toast.success('Mensaje WhatsApp enviado correctamente');
      else toast.error(`Error: ${JSON.stringify(data.body)}`);
    } catch { toast.error('Error al enviar'); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-black text-gray-900">Integraciones</h1>
          <HelpDrawer section="integrations" />
        </div>
        <p className="text-gray-500 mt-1">Gestiona las conexiones con servicios externos de comunicación</p>
      </div>

      {/* Status Overview */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="border-2 border-gray-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Kapso</h3>
                  <p className="text-xs text-gray-500">WhatsApp Business API</p>
                </div>
              </div>
              <StatusBadge configured={status.kapso?.configured} loading={loadingStatus} />
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Kapso gestiona todos los mensajes de WhatsApp: confirmaciones, recordatorios, notificaciones de walk-in y comunicaciones de marketing.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={testKapso} disabled={testingKapso} variant="outline" className="gap-1 flex-1">
                {testingKapso ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />} Test conexión
              </Button>
              <a href="https://kapso.ai" target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="ghost" className="gap-1"><ExternalLink className="w-3.5 h-3.5" /> Panel</Button>
              </a>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-gray-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">SendPulse</h3>
                  <p className="text-xs text-gray-500">Email Marketing & Transaccional</p>
                </div>
              </div>
              <StatusBadge configured={status.sendpulse?.configured} loading={loadingStatus} />
            </div>
            <p className="text-sm text-gray-600 mb-3">
              SendPulse maneja todos los envíos de email: confirmaciones, NPS, campañas, encuestas post-visita y notificaciones del sistema.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={testSendPulse} disabled={testingSP} variant="outline" className="gap-1 flex-1">
                {testingSP ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />} Test conexión
              </Button>
              <a href="https://sendpulse.com" target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="ghost" className="gap-1"><ExternalLink className="w-3.5 h-3.5" /> Panel</Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tabs */}
      <Tabs defaultValue="kapso">
        <TabsList>
          <TabsTrigger value="kapso" className="gap-1.5"><MessageCircle className="w-4 h-4" /> Kapso (WhatsApp)</TabsTrigger>
          <TabsTrigger value="sendpulse" className="gap-1.5"><Mail className="w-4 h-4" /> SendPulse (Email)</TabsTrigger>
        </TabsList>

        <TabsContent value="kapso" className="space-y-5 mt-6">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Key className="w-4 h-4 text-orange-500" /> Credenciales API</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4 border">
                <p className="text-sm text-gray-600 mb-3">El API key de Kapso se gestiona como secreto seguro del servidor:</p>
                <div className="flex items-center gap-2 bg-black rounded-lg px-4 py-3">
                  <code className="text-orange-400 text-sm font-mono flex-1">KAPSO_API_KEY</code>
                  <StatusBadge configured={status.kapso?.configured} loading={loadingStatus} />
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Para actualizar el API key, ve al panel de <strong>Secrets</strong> y modifica <code className="bg-blue-100 px-1 rounded">KAPSO_API_KEY</code>. Los cambios se aplican automáticamente.
                </p>
              </div>
              <Button onClick={testKapso} disabled={testingKapso} className="bg-green-600 hover:bg-green-700 text-white gap-2">
                {testingKapso ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                {testingKapso ? 'Verificando...' : 'Verificar conexión con Kapso'}
              </Button>
            </CardContent>
          </Card>

          {kapsoChannels.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Phone className="w-4 h-4 text-orange-500" /> Canales WhatsApp disponibles</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {kapsoChannels.map((ch, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-green-500" />
                        <span className="font-medium text-sm">{ch.name || ch.phone_number || `Canal ${i + 1}`}</span>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700">Activo</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4 text-orange-500" /> Enviar WhatsApp de Prueba</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Número de destino (con código de país)</Label>
                  <Input value={testWhatsApp.phone} onChange={e => setTestWhatsApp(p => ({ ...p, phone: e.target.value }))} placeholder="+52 55 1234 5678" />
                </div>
                {kapsoChannels.length > 0 && (
                  <div>
                    <Label>Channel ID</Label>
                    <Input value={testWhatsApp.channel_id} onChange={e => setTestWhatsApp(p => ({ ...p, channel_id: e.target.value }))} />
                  </div>
                )}
                <div className="col-span-2">
                  <Label>Mensaje</Label>
                  <Textarea value={testWhatsApp.message} onChange={e => setTestWhatsApp(p => ({ ...p, message: e.target.value }))} rows={2} />
                </div>
              </div>
              <Button onClick={sendTestWhatsApp} disabled={!status.kapso?.configured} className="bg-green-600 hover:bg-green-700 text-white gap-2">
                <MessageCircle className="w-4 h-4" /> Enviar WhatsApp de prueba
              </Button>
              {!status.kapso?.configured && <p className="text-xs text-gray-400">Configura KAPSO_API_KEY primero</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4 text-orange-500" /> Casos de uso activos</CardTitle></CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  ['Confirmación de reservación', true],
                  ['Recordatorio 24h antes', true],
                  ['Notificación walk-in listo', true],
                  ['Encuesta NPS post-visita', true],
                  ['Alerta de no-show', true],
                  ['Campaña de reactivación', false],
                ].map(([label, active], i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg">
                    <div className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    <span className="text-sm text-gray-700 flex-1">{label}</span>
                    <Badge className={`text-xs ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{active ? 'Activo' : 'Próximamente'}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sendpulse" className="space-y-5 mt-6">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Key className="w-4 h-4 text-orange-500" /> Credenciales API</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4 border space-y-2">
                <p className="text-sm text-gray-600 mb-2">Credenciales de SendPulse gestionadas como secretos del servidor:</p>
                {['SENDPULSE_API_USER_ID', 'SENDPULSE_API_SECRET'].map(key => (
                  <div key={key} className="flex items-center gap-2 bg-black rounded-lg px-4 py-2.5">
                    <code className="text-orange-400 text-sm font-mono flex-1">{key}</code>
                    <StatusBadge configured={status.sendpulse?.configured} loading={loadingStatus} />
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Obtén tus credenciales en <a href="https://sendpulse.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">sendpulse.com → API → OAuth</a>. Guárdalas en el panel de Secrets.
                </p>
              </div>
              <Button onClick={testSendPulse} disabled={testingSP} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                {testingSP ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                {testingSP ? 'Verificando...' : 'Verificar conexión con SendPulse'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4 text-orange-500" /> Casos de uso activos</CardTitle></CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  ['Confirmación de reservación por email', true],
                  ['Bienvenida a programa de lealtad', true],
                  ['Encuesta NPS post-visita', true],
                  ['Resumen semanal para restaurantes', true],
                  ['Factura CFDI por email', true],
                  ['Campaña de cumpleaños', true],
                  ['Newsletter de novedades', false],
                  ['Automatización de reactivación', false],
                ].map(([label, active], i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg">
                    <div className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    <span className="text-sm text-gray-700 flex-1">{label}</span>
                    <Badge className={`text-xs ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{active ? 'Activo' : 'Próximamente'}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Globe className="w-4 h-4 text-orange-500" /> Configuración adicional recomendada</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {[
                ['1', 'Verificar dominio de envío', 'En SendPulse → Senders → Verifica tu dominio para mejorar la entregabilidad.'],
                ['2', 'Configurar DKIM y SPF', 'Agrega los registros DNS en tu proveedor de dominio para autenticar los emails.'],
                ['3', 'Crear listas de suscriptores', 'Configura listas separadas para restaurantes y comensales en tu panel de SendPulse.'],
                ['4', 'Diseñar plantillas de email', 'Crea plantillas HTML con tu branding para cada tipo de notificación.'],
              ].map(([step, title, desc]) => (
                <div key={step} className="flex gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{step}</div>
                  <div><p className="font-semibold text-sm text-gray-900">{title}</p><p className="text-xs text-gray-500">{desc}</p></div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
